import { useState } from 'react';
import { Input, Button, Spinner } from '@heroui/react';
import { Plus, Search } from 'lucide-react';
import { useCompanies } from '@/api/companies';
import SelectableChip from '@/components/common/SelectableChip';
import CompanyForm from './CompanyForm';
import { CompanyType } from '@estimator/shared';
import type { Company, CompanyTypeEnum } from '@estimator/shared';

interface CompanyListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const TYPE_FILTERS: CompanyTypeEnum[] = [
  CompanyType.CONTRACTOR, CompanyType.ARCHITECT, CompanyType.MANUFACTURER, CompanyType.DESIGNER, CompanyType.SUPPLIER,
];

const TYPE_COLORS: Record<string, string> = {
  CONTRACTOR: '#86efac', ARCHITECT: '#a5b4fc', MANUFACTURER: '#fbbf24',
  DESIGNER: '#d8b4fe', GENERAL_CONTRACTOR: '#86efac', SUBCONTRACTOR: '#67e8f9',
  SUPPLIER: '#fca5a5', OTHER: '#94a3b8',
};

export default function CompanyList({ selectedId, onSelect }: CompanyListProps) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<CompanyTypeEnum | undefined>();
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useCompanies({
    search: search || undefined,
    type: typeFilter,
  });
  const companies = data?.data ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-[var(--border)]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Companies
          </h2>
          <Button
            size="sm"
            color="primary"
            onPress={() => setShowForm(true)}
            startContent={<Plus size={14} />}
          >
            New
          </Button>
        </div>

        <Input
          size="sm"
          placeholder="Search companies..."
          value={search}
          onValueChange={setSearch}
          startContent={<Search size={14} />}
          variant="bordered"
          classNames={{
            inputWrapper: 'border border-[var(--border)] bg-[var(--bg-panel)]',
          }}
        />

        <div className="flex gap-1 mt-2 flex-wrap">
          <SelectableChip
            selected={!typeFilter}
            onToggle={() => setTypeFilter(undefined)}
          >
            All
          </SelectableChip>
          {TYPE_FILTERS.map((t) => (
            <SelectableChip
              key={t}
              selected={typeFilter === t}
              onToggle={() => setTypeFilter(typeFilter === t ? undefined : t)}
            >
              {t.charAt(0) + t.slice(1).toLowerCase()}
            </SelectableChip>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : companies.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: 'var(--text-secondary)' }}>
            No companies yet
          </p>
        ) : (
          companies.map((company: Company) => (
            <button
              key={company.id}
              className="w-full text-left px-3 py-2.5 transition-colors"
              style={{
                borderLeft: selectedId === company.id
                  ? '3px solid var(--accent)'
                  : '3px solid transparent',
                backgroundColor: selectedId === company.id
                  ? 'var(--bg-surface)'
                  : 'transparent',
              }}
              onClick={() => onSelect(company.id)}
            >
              <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {company.name}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                {company.types?.map((t: string) => (
                  <span
                    key={t}
                    className="text-[9px] px-1.5 py-0.5 rounded-full"
                    style={{
                      backgroundColor: `${TYPE_COLORS[t] ?? '#94a3b8'}22`,
                      color: TYPE_COLORS[t] ?? '#94a3b8',
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </button>
          ))
        )}
      </div>

      <CompanyForm isOpen={showForm} onClose={() => setShowForm(false)} />
    </div>
  );
}
